import { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import { petTypeForSpecies } from '../lib/analytics/cartSnapshot';
import { cartHoldsRescueBundle } from '../lib/rescueCheckout';
import { cartActions } from '../reducers/storeCart';
import * as types from '../types';
import { Species } from '../types/gql-op-types';
import useRescueBundleSkus from './useRescueBundleSkus';
import useTrackAddedItemToCart from './useTrackAddedItemToCart';

/**
 * Puts the adoption bundle in the cart, which is what the one-page checkout prices and buys. On the pdp
 * this was the Add to Cart tap; here the page is the checkout, so an empty cart is filled on arrival and
 * reported the way that tap is (`trackAddedItemToCart`: Customer.io's added-to-cart, which abandoned-cart
 * email reads). Returns whether the cart now holds exactly the bundle, so the checkout can wait for it.
 *
 * Only an empty cart is filled: a cart holding anything else never reaches this page
 * (`cartFitsRescueCheckout`). Idempotent: it reads the store at the moment it runs rather than the
 * render's snapshot, so Strict Mode's double run and a reload find the bundle there and add nothing. The
 * adoption coupon is not applied here: `useAutoApplyCoupon` applies it once the cart has an item, as it
 * does after the pdp's Add to Cart.
 */
export default function useRescueCartSeed(species: Species) {
  const store = useStore<types.AppState>();
  const dispatch = useDispatch();
  const { currency } = useLocale();
  const skus = useRescueBundleSkus();
  const { miniSku, planSku } = skus;
  const trackAddedItemToCart = useTrackAddedItemToCart();
  const seeded = useSelector((state: types.AppState) => cartHoldsRescueBundle(state.storeShop.cart, skus));

  useEffect(() => {
    if (!miniSku || !planSku || Object.keys(store.getState().storeShop.cart.cartItems).length > 0) {
      return;
    }
    dispatch(
      cartActions.addBundleCartItem({
        sku: miniSku,
        subscriptionSku: planSku,
        addonSkus: [],
        currency,
        isGift: false,
        species,
      }),
    );
    void trackAddedItemToCart(miniSku, petTypeForSpecies(species));
  }, [store, dispatch, miniSku, planSku, currency, species, trackAddedItemToCart]);

  return seeded;
}
